import CurrencyInput from '@ui/Input/masked/CurrencyInput';
import useDebounce from '@util/hooks/useDebounce';
import { useEffect } from 'react';
import { ChangeEvent, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface PriceRangeFilterSectionProps {
  title?: string;
  onChange: (min: number | null, max: number | null) => void;
  onSearch?: () => void;
  initialMin: number | null;
  initialMax: number | null;
  showIcon?: boolean;
}

const PriceRangeFilterSection = ({
  title,
  onChange,
  onSearch,
  initialMax,
  initialMin,
  showIcon = false
}: PriceRangeFilterSectionProps) => {
  const [state, setState] = useState({
    max: initialMax || null,
    min: initialMin || null,
  });
  const [error, setError] = useState('');
  const debouncedState = useDebounce(state, 500);
  useEffect(() => {
    // Only call onChange if the values have changed
    if (debouncedState.min !== initialMin || debouncedState.max !== initialMax)
      onChange(debouncedState.min, debouncedState.max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedState, error]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const val = e.target.value ?? '';
    const stripped = val.replace(/[,.]/g, '');
    const intValue = e.target.value ? parseInt(stripped) : null;
    setState((prev) => ({ ...prev, [name]: intValue }));
  };

  const onMinBlur = () => {
    if (state?.min !== initialMin) onChange(state?.min, state?.max);
  };
  const onMaxBlur = () => {
    if (state?.max !== initialMax) onChange(state?.min, state?.max);
  };

  useEffect(() => {
    if (!initialMin && !initialMax) setError('');
    if (initialMin && initialMax && initialMin > initialMax) {
      setError('Min should not be greater than max');
    }
  }, [initialMax, initialMin]);

  return (
    <div>
      {title && (
        <div className="font-medium text-[17px] pb-[1rem]">
          {title}
        </div>
      )}

      <div className="flex w-full items-center gap-[0.8rem] p-[1.2rem]">
        <div className="flex max-w-lg grow flex-row items-center justify-between rounded-[10px] border-[1px] px-[0.8rem] py-[0.8rem]">
          <span className="ml-[1.2rem] mr-[0.8rem] text-[1.5rem] font-semibold text-brand-black ">
            $
          </span>
          <CurrencyInput
            placeholder={'Min'}
            value={state.min ?? ''}
            onChange={(e) => handleInputChange(e, 'min')}
            onBlur={onMinBlur}
            error={!!error}
          />
        </div>
        <p className="">-</p>
        <div className="flex max-w-lg grow flex-row items-center justify-between rounded-[10px] border-[1px] px-[0.8rem] py-[0.8rem]">
          <span className="ml-[1.2rem] mr-[0.8rem] text-[1.5rem] font-semibold text-brand-black ">
            $
          </span>
          <CurrencyInput
            placeholder={'Max'}
            value={state.max ?? ''}
            onChange={(e) => handleInputChange(e, 'max')}
            onBlur={onMaxBlur}
            error={!!error}
          />
        </div>
        {showIcon && (
          <MagnifyingGlassIcon className="ml-2 h-12 w-12 text-gray-400 cursor-pointer" 
            onClick={() => onSearch ? onSearch() : undefined} 
          />
        )}
      </div>
      {error && (
        <p className="text-center text-[1.3rem] font-semibold text-brand-red">
          {error}
        </p>
      )}
    </div>
  );
};

export default PriceRangeFilterSection;
